import { useState } from 'react';

import { faCircleNotch, faCog, faList, faStar, faTimeline } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate } from 'react-router-dom';

import { TrackingEvents } from '../../../../../../analytics/events';
import ActionButton from '../ActionButton';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { ApiError, CreateChronologyResponse, useCreateChronology } from '@/api/mutations/useCreateChronology';

interface ChronologyModalProps {
  caseId: string | null;
  totalKeyFacts: number;
  totalRelevantFacts: number;
}

const ChronologyModal = ({ caseId, totalKeyFacts, totalRelevantFacts }: ChronologyModalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const onSuccess = (data: CreateChronologyResponse) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('chronologyId', data.chronologyId);
    searchParams.set('page', '1');
    navigate('/app/chronos/case-editor/data/chronology/view?' + searchParams.toString());
  };

  const onError = (error: ApiError) => {
    console.error('Mutation failed:', error);
    // Handle error scenario here
    setIsLoading(false);
  };

  const { createChronology } = useCreateChronology({ onSuccess, onError });

  const handleChronologyCreation = async (type: string) => {
    setIsLoading(true);
    createChronology.mutate({
      caseId,
      chronologyId: null,
      type: type,
    });
  };

  const handleCustomChronology = () => {
    const searchParams = new URLSearchParams(location.search);
    const caseId = searchParams.get('caseId');
    if (!caseId) return;
    searchParams.set('caseId', caseId);
    searchParams.set('chronologyTour', 'true');
    searchParams.set('page', '0');
    navigate('/app/chronos/case-editor/data/facts?' + searchParams.toString());
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 p-6">
        <h2 className="flex items-center gap-3 text-lg font-semibold text-gray-900">
          <FontAwesomeIcon icon={faTimeline} className="h-5 w-5 text-blue-500" />
          Create Chronology
        </h2>
      </div>
      {isLoading ? (
        <div className="flex h-80 flex-col items-center justify-center gap-6 p-6">
          <p className="text-sm text-gray-600">Please wait while we construct your chronology...</p>
          <FontAwesomeIcon icon={faCircleNotch} className="h-10 w-10 animate-spin" />
        </div>
      ) : (
        <div className="p-6">
          <div className="mb-6 text-sm leading-relaxed text-gray-600">
            Create a chronology from our preset options, or discover how to create your own from the full body of facts.
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              <ActionButton
                title="Key Facts"
                description={
                  <span>
                    Create chronology from <b className="text-blue-500">{totalKeyFacts}</b> key facts
                  </span>
                }
                icon={<FontAwesomeIcon icon={faStar} className="h-4 w-4 text-yellow-500" />}
                onClick={() => {
                  handleChronologyCreation('keyFacts');
                  trackCustomEvent(TrackingEvents.CreateKeyFactsChronology, { caseId });
                }}
              />
              <ActionButton
                title="Relevant Facts"
                description={
                  <span>
                    Create chronology from <b className="text-blue-500">{totalRelevantFacts}</b> relevant facts
                  </span>
                }
                icon={<FontAwesomeIcon icon={faList} className="h-4 w-4 text-blue-500" />}
                onClick={() => {
                  handleChronologyCreation('relevantFacts');
                  trackCustomEvent(TrackingEvents.CreateRelevantFactsChronology, { caseId });
                }}
              />
            </div>
            <div className="border-t border-gray-200 pt-4">
              <ActionButton
                title="Custom"
                description="See how you can build your own chronology"
                icon={<FontAwesomeIcon icon={faCog} className="h-4 w-4 text-gray-500" />}
                onClick={() => {
                  handleCustomChronology();
                  trackCustomEvent(TrackingEvents.ViewCustomChronologyFromSummary, { caseId });
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChronologyModal;
